import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Gamepad2, Lock, Trophy, Star, Zap, Shield, Search, Play, Sparkles } from 'lucide-react';
import { useAcademyStore } from '../../store/academyStore';
import { useSfx } from '../../store/sfxStore';

interface Game {
    id: string;
    title: string;
    description: string;
    icon: string;
    route: string;
    requiredModule: string;
    requiredModuleName: string;
    course: string;
    color: string;
    difficulty: 'easy' | 'medium' | 'hard';
}

const GAMES: Game[] = [
    {
        id: 'variable-tetris',
        title: 'Variable Tetris',
        description: 'Catch falling values in the correct data type buckets. Test your knowledge of strings, numbers, booleans, and more!',
        icon: '🎮',
        route: '/games/variable-tetris',
        requiredModule: 'variables-types',
        requiredModuleName: 'Backpack Inventory',
        course: 'Foundation',
        color: 'from-green-500 to-emerald-600',
        difficulty: 'easy',
    },
    {
        id: 'hack-defense',
        title: 'Hack Defense',
        description: 'Defend your network using the CIA Triad! Place Confidentiality, Integrity, and Availability defenses to block cyber attacks.',
        icon: '🛡️',
        route: '/games/hack-defense',
        requiredModule: 'cia-triad',
        requiredModuleName: 'The Shield',
        course: 'Cyber Security',
        color: 'from-red-500 to-rose-600',
        difficulty: 'medium',
    },
    {
        id: 'query-quest',
        title: 'Query Quest',
        description: 'Become a data detective! Solve mysteries by writing SQL queries to find the evidence you need.',
        icon: '🔍',
        route: '/games/query-quest',
        requiredModule: 'data-detective-sql',
        requiredModuleName: 'The Library',
        course: 'Data Detective',
        color: 'from-amber-500 to-orange-600',
        difficulty: 'medium',
    },
    {
        id: 'flexbox-froggy',
        title: 'Flexbox Froggy',
        description: 'Help the frogs find their lilypads! Write CSS Flexbox code to move them around the pond.',
        icon: '🐸',
        route: '/games/flexbox-froggy',
        requiredModule: 'box-model',
        requiredModuleName: 'Flexbox Playground', // Using Box Model for now as prereq
        course: 'UI/UX Design',
        color: 'from-pink-500 to-rose-600',
        difficulty: 'easy',
    },
    {
        id: 'loop-racer',
        title: 'Loop Racer',
        description: 'Start your engines! Write the correct loop to drive your car to the finish line.',
        icon: '🏎️',
        route: '/games/loop-racer',
        requiredModule: 'loops-arrays',
        requiredModuleName: 'Banana Counting',
        course: 'Foundation',
        color: 'from-blue-500 to-indigo-600',
        difficulty: 'easy',
    },
    // Future games - placeholder for when they're built
    {
        id: 'webhook-catcher',
        title: 'Webhook Catcher Pro',
        description: 'Catch incoming webhook payloads with power-ups, combos, and difficulty levels!',
        icon: '🎣',
        route: '/games/webhook-catcher',
        requiredModule: 'webhooks',
        requiredModuleName: 'The Catcher',
        course: 'Automation',
        color: 'from-orange-500 to-amber-600',
        difficulty: 'easy',
    },
    {
        id: 'function-factory',
        title: 'Function Factory',
        description: 'Build logic machines! Connect inputs to operations to get the right output.',
        icon: '🏭',
        route: '/games/function-factory',
        requiredModule: 'functions-scope',
        requiredModuleName: 'The Function Oven',
        course: 'Foundation',
        color: 'from-purple-500 to-fuchsia-600',
        difficulty: 'medium',
    },
    {
        id: 'grid-garden',
        title: 'Grid Garden',
        description: 'Water the garden using CSS Grid! Plant carrots and weed out the pests.',
        icon: '🥕',
        route: '/games/grid-garden',
        requiredModule: 'css-grid',
        requiredModuleName: 'Grid Master',
        course: 'UI/UX Design',
        color: 'from-green-600 to-lime-600',
        difficulty: 'hard',
    },
    {
        id: 'bump-and-bounce',
        title: 'Bump & Bounce Arcade',
        description: 'Dodge the vicious snakes, collect bananas, and snatch invincibility stars! Survive endless waves of enemies!',
        icon: '🦍',
        route: '/games/bump-and-bounce',
        requiredModule: 'sprite-collision',
        requiredModuleName: 'Bump & Bounce',
        course: 'Game Development',
        color: 'from-emerald-500 to-green-700',
        difficulty: 'medium',
    },
    {
        id: 'state-sorter',
        title: 'State Sorter Engine',
        description: 'Sort incoming concepts into the correct memory bucket. Don\'t mix up your State, Props, and Constants!',
        icon: '📦',
        route: '/games/state-sorter',
        requiredModule: 'react-state',
        requiredModuleName: 'State Machine',
        course: 'Logic Architect',
        color: 'from-blue-500 to-cyan-600',
        difficulty: 'medium',
    },
    {
        id: 'regex-asteroids',
        title: 'Regex Asteroids',
        description: 'Type regular expressions to destroy incoming server log anomalies before they breach your system.',
        icon: '☄️',
        route: '/games/regex-asteroids',
        requiredModule: 'server-monitoring',
        requiredModuleName: 'Server Monitoring',
        course: 'Sovereign Systems',
        color: 'from-green-600 to-emerald-800',
        difficulty: 'hard',
    },
    {
        id: 'grid-invaders',
        title: 'CSS Grid Invaders',
        description: 'Defend your layout from alien bugs! Set the correct grid-column and grid-row coordinates to fire your turret.',
        icon: '👾',
        route: '/games/grid-invaders',
        requiredModule: 'css-grid',
        requiredModuleName: 'Grid Master',
        course: 'UI/UX Design',
        color: 'from-pink-600 to-rose-800',
        difficulty: 'medium',
    },
];

const getDifficultyBadge = (difficulty: Game['difficulty']) => {
    switch (difficulty) {
        case 'easy':
            return <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full">Easy</span>;
        case 'medium':
            return <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-bold rounded-full">Medium</span>;
        case 'hard':
            return <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded-full">Hard</span>;
    }
};

export const GamesHub = () => {
    const { currentUser, progress } = useAcademyStore();
    const { play } = useSfx();
    const userProgress = currentUser ? progress[currentUser.id] : null;

    const isModuleCompleted = (moduleId: string): boolean => {
        if (!userProgress?.completedModules) return false;
        return userProgress.completedModules.includes(moduleId);
    };

    const getHighScore = (gameId: string): number => {
        const key = `${gameId}-highscore`;
        const saved = localStorage.getItem(key);
        return saved ? parseInt(saved) : 0;
    };

    const unlockedGames = GAMES.filter(g => isModuleCompleted(g.requiredModule));
    const lockedGames = GAMES.filter(g => !isModuleCompleted(g.requiredModule));

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 font-sans text-white">
            {/* Header */}
            <div className="bg-slate-800/80 backdrop-blur-xl border-b border-slate-700 px-8 py-4 flex justify-between items-center sticky top-0 z-50">
                <Link to="/" className="flex items-center gap-2 text-slate-400 font-bold hover:text-purple-400 transition-colors">
                    <Home size={20} /> Dashboard
                </Link>
                <div className="bg-purple-900/50 text-purple-400 px-4 py-2 rounded-xl text-base font-bold border border-purple-800 flex items-center gap-2">
                    <Gamepad2 size={20} /> Games Hub
                </div>
            </div>

            <div className="max-w-6xl mx-auto p-8">
                {/* Title */}
                <div className="text-center mb-12">
                    <motion.h1
                        className="text-5xl font-black text-white mb-4 flex items-center justify-center gap-4"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <Gamepad2 className="text-purple-400" size={56} />
                        Arcade Zone
                    </motion.h1>
                    <p className="text-xl text-slate-400 max-w-2xl mx-auto">
                        Practice your skills with fun mini-games! Complete modules to unlock new games.
                    </p>

                    {/* Stats */}
                    <div className="flex justify-center gap-6 mt-8">
                        <div className="bg-slate-800/50 px-6 py-4 rounded-2xl border border-slate-700">
                            <div className="text-3xl font-black text-purple-400">{unlockedGames.length}</div>
                            <div className="text-sm text-slate-500 font-bold">Unlocked</div>
                        </div>
                        <div className="bg-slate-800/50 px-6 py-4 rounded-2xl border border-slate-700">
                            <div className="text-3xl font-black text-slate-500">{lockedGames.length}</div>
                            <div className="text-sm text-slate-500 font-bold">Locked</div>
                        </div>
                        <div className="bg-slate-800/50 px-6 py-4 rounded-2xl border border-slate-700">
                            <div className="text-3xl font-black text-yellow-400">{GAMES.length}</div>
                            <div className="text-sm text-slate-500 font-bold">Total Games</div>
                        </div>
                    </div>
                </div>

                {/* Unlocked Games */}
                {unlockedGames.length > 0 && (
                    <div className="mb-12">
                        <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                            <Sparkles className="text-green-400" /> Ready to Play
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {unlockedGames.map((game, index) => {
                                const highScore = getHighScore(game.id);
                                return (
                                    <motion.div
                                        key={game.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                    >
                                        <Link
                                            to={game.route}
                                            className="block group"
                                        >
                                            <div className={`relative p-6 rounded-3xl bg-gradient-to-br ${game.color} overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl`}>
                                                {/* Shine Effect */}
                                                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

                                                <div className="relative z-10 flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-3 mb-3">
                                                            <span className="text-5xl">{game.icon}</span>
                                                            <div>
                                                                <h3 className="text-2xl font-bold text-white">{game.title}</h3>
                                                                <div className="flex items-center gap-2 mt-1">
                                                                    <span className="text-xs font-bold text-white/70 bg-white/20 px-2 py-0.5 rounded">{game.course}</span>
                                                                    {getDifficultyBadge(game.difficulty)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <p className="text-white/80 text-base leading-relaxed">{game.description}</p>
                                                    </div>
                                                </div>

                                                {/* High Score & Play Button */}
                                                <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/20">
                                                    <div className="flex items-center gap-4">
                                                        {highScore > 0 && (
                                                            <div className="flex items-center gap-2 text-white/80">
                                                                <Trophy size={18} />
                                                                <span className="font-bold">High Score: {highScore}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-xl font-bold group-hover:bg-white/30 transition-colors">
                                                        <Play size={18} fill="currentColor" /> Play Now
                                                    </div>
                                                </div>
                                            </div>
                                        </Link>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Locked Games */}
                {lockedGames.length > 0 && (
                    <div>
                        <h2 className="text-2xl font-bold text-slate-400 mb-6 flex items-center gap-3">
                            <Lock /> Complete Modules to Unlock
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {lockedGames.map((game, index) => (
                                <motion.div
                                    key={game.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 + unlockedGames.length * 0.1 }}
                                    className="relative p-6 rounded-3xl bg-slate-800/50 border-2 border-slate-700 overflow-hidden opacity-60 cursor-not-allowed"
                                    onClick={() => play('error')}
                                >
                                    {/* Lock Overlay */}
                                    <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm z-10 flex items-center justify-center">
                                        <div className="text-center">
                                            <Lock size={48} className="text-slate-500 mx-auto mb-3" />
                                            <p className="text-slate-400 font-bold">Complete "{game.requiredModuleName}"</p>
                                            <p className="text-slate-500 text-sm">in {game.course} course</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4">
                                        <span className="text-4xl grayscale opacity-50">{game.icon}</span>
                                        <div>
                                            <h3 className="text-xl font-bold text-slate-400">{game.title}</h3>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="text-xs font-bold text-slate-500 bg-slate-700 px-2 py-0.5 rounded">{game.course}</span>
                                            </div>
                                            <p className="text-slate-500 text-sm mt-2">{game.description}</p>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Empty State */}
                {unlockedGames.length === 0 && (
                    <div className="text-center py-16">
                        <div className="text-6xl mb-6">🎮</div>
                        <h3 className="text-2xl font-bold text-white mb-3">No Games Unlocked Yet</h3>
                        <p className="text-slate-400 mb-6">Complete modules to unlock practice games!</p>
                        <Link
                            to="/"
                            className="btn bg-gradient-to-r from-purple-500 to-violet-600 text-white shadow-xl"
                        >
                            Back to Dashboard
                        </Link>
                    </div>
                )}

                {/* Coming Soon */}
                <div className="mt-16 text-center">
                    <h3 className="text-xl font-bold text-slate-500 mb-4">🚀 More Games Coming Soon!</h3>
                    <div className="flex justify-center flex-wrap gap-4">
                        {['Container Commander', 'API Tycoon', 'Cyber Shield Pro'].map(name => (
                            <span key={name} className="px-4 py-2 bg-slate-800 text-slate-500 rounded-full text-sm font-bold border border-slate-700">
                                {name}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
